import type { ReactNode } from "react";
import type { RoomView, GameView, Action } from "../../server/protocol";
export type PlayProps = {
  state: RoomView & { game: GameView };
  send: (action: Action) => unknown;
  disabled: boolean;
  conceal?: boolean;
  isDisplay: boolean;
  onRules?: () => void;
  /** An action of this seat is awaiting server acknowledgement (separate from pause/offline). */
  pending?: boolean;
  /** Last rejected-action message for this seat, shown next to the control that failed. */
  error?: string | null;
};
export type PublicPlayer = { id: string; name: string; alive?: boolean };
export type GameLayoutProps = PlayProps & {
  title: string;
  description?: ReactNode;
  board: ReactNode;
  action?: ReactNode;
  privateContent?: ReactNode;
  privateAction?: ReactNode;
  history?: ReactNode;
  phaseLabel?: string;
  /** Second context chip (public progress, score). Shown to every audience, so
   * it must derive only from public fields. */
  phaseNote?: ReactNode;
  privatePresentation?: "dialog" | "adaptive-inline";
  privateLabel?: string;
  privateInlineContent?: ReactNode;
  composition?: "split" | "stacked";
  contextArt?: ReactNode;
  publicTitle?: string;
  publicDescription?: ReactNode;
  publicBoard?: ReactNode;
  /** How long a deliberate reveal holds: the phase (default), the round (a Dice
   * hand) or the match (a word, location or role that never changes). Every
   * other cover trigger applies regardless. */
  privateScope?: "phase" | "round" | "match";
  /** Shown on the covered entry when the hand changed since it was last seen. */
  privateCoverNote?: string;
  /** Own-seat title used until the private card has been opened in this scope
   * (and the private entry pulses once). */
  unrevealedTitle?: string;
  /** Word after the public submitted count ("voted", "guesses locked"). */
  progressLabel?: string;
};
export function playerName(
  players: PublicPlayer[],
  id: string | null | undefined,
) {
  const i = players.findIndex((p) => p.id === id);
  if (i < 0) return "—";
  const name = players[i].name;
  return players.filter((p) => p.name === name).length > 1
    ? `${name} #${i + 1}`
    : name;
}
